import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthor } from '@/hooks/useAuthor';
import type { WisdomLetter } from '@/hooks/useWisdomLetters';

interface LetterCardProps {
  letter: WisdomLetter;
}

const TOPIC_COLORS: Record<string, string> = {
  relationships: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  career: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  health: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  money: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  family: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  mindset: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  education: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  travel: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

function getTopicClass(topic: string): string {
  return TOPIC_COLORS[topic.toLowerCase()] ?? 'bg-accent text-accent-foreground';
}

export function LetterCard({ letter }: LetterCardProps) {
  const author = useAuthor(letter.event.pubkey);
  const metadata = author.data?.metadata;

  const noteId = nip19.noteEncode(letter.event.id);
  const displayName = metadata?.name ?? `${letter.event.pubkey.slice(0, 8)}…`;
  const avatar = metadata?.picture;
  const initials = displayName.slice(0, 2).toUpperCase();
  const preview = letter.body.slice(0, 220).replace(/\n+/g, ' ').trim();
  const hasMore = letter.body.length > 220;
  const timeAgo = formatDistanceToNow(new Date(letter.event.created_at * 1000), { addSuffix: true });

  return (
    <Link to={`/${noteId}`} className="group block">
      <Card className="h-full border-border bg-card hover:shadow-md transition-all duration-200 group-hover:border-primary/30">
        <CardHeader className="pb-3">
          {/* Author row */}
          <div className="flex items-center gap-2.5 mb-3">
            <Avatar className="w-8 h-8">
              <AvatarImage src={avatar} alt={displayName} />
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground">{timeAgo}</p>
            </div>
          </div>

          {/* Title */}
          <h2 className="font-serif font-semibold text-lg leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {letter.title}
          </h2>

          {/* Subject */}
          <p className="text-sm text-muted-foreground italic">
            To: {letter.subject}
          </p>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Preview */}
          <p className="text-sm text-foreground/80 leading-relaxed font-serif line-clamp-3">
            {preview}{hasMore && '…'}
          </p>

          {/* Topics */}
          {letter.topics.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {letter.topics.slice(0, 4).map((topic) => (
                <span
                  key={topic}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTopicClass(topic)}`}
                >
                  {topic}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
